if (!customElements.get('ld-retail-gallery')) {
  customElements.define('ld-retail-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.photos = [...this.querySelectorAll('.ld-retail-gallery__photo')];
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('.ld-retail-gallery__stage');
      this.image = this.querySelector('.ld-retail-gallery__full');
      this.feedback = this.querySelector('.ld-retail-gallery__feedback');
      this.status = this.feedback.querySelector('[role="status"]');
      this.count = this.querySelector('.ld-retail-gallery__count');
      this.retry = this.querySelector('[data-retail-retry]');
      this.requestId = 0;
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('.ld-retail-gallery__track');
      const trackControls = this.querySelector('[data-retail-track-controls]');
      if (trackControls) {
        const previous = trackControls.querySelector('[data-retail-track-prev]');
        const next = trackControls.querySelector('[data-retail-track-next]');
        const updateControls = () => {
          const overflow = this.track.scrollWidth - this.track.clientWidth;
          trackControls.hidden = overflow < 2;
          previous.disabled = this.track.scrollLeft < 2;
          next.disabled = this.track.scrollLeft >= overflow - 2;
        };
        const scroll = direction => {
          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          this.track.scrollBy({
            left: direction * (this.track.clientWidth + gap),
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        };
        previous.addEventListener('click', () => scroll(-1), options);
        next.addEventListener('click', () => scroll(1), options);
        this.track.addEventListener('scroll', updateControls, { ...options, passive: true });
        this.trackObserver = new ResizeObserver(updateControls);
        this.trackObserver.observe(this.track);
        updateControls();
      }
      for (const [index, photo] of this.photos.entries()) {
        photo.addEventListener('click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.trigger = photo;
          this.dialog.showModal();
          this.load(index);
        }, options);
      }
      this.querySelector('[data-retail-prev]').addEventListener('click', () => this.load(this.index - 1), options);
      this.querySelector('[data-retail-next]').addEventListener('click', () => this.load(this.index + 1), options);
      this.querySelector('[data-retail-close]').addEventListener('click', () => this.dialog.close(), options);
      this.retry.addEventListener('click', () => this.load(this.index), options);
      this.dialog.addEventListener('close', () => {
        this.requestId++;
        clearTimeout(this.timeout);
        this.trigger?.focus();
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.load(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
    }

    async load(index) {
      this.index = (index + this.photos.length) % this.photos.length;
      const requestId = ++this.requestId;
      clearTimeout(this.timeout);
      this.stage.setAttribute('aria-busy', 'true');
      this.feedback.hidden = false;
      this.feedback.classList.remove('is-error');
      this.retry.hidden = true;
      this.status.textContent = this.dataset.loading;
      const photo = this.photos[this.index];
      const next = new Image();
      next.className = this.image.className;
      next.alt = photo.querySelector('img').alt;
      next.width = 1200;
      next.height = 1500;
      const url = new URL(photo.href);
      url.searchParams.set('width', String(Math.min(1600, Math.ceil(this.dialog.clientWidth * Math.min(devicePixelRatio, 2)))));
      try {
        await Promise.race([
          new Promise((resolve, reject) => {
            next.onload = resolve;
            next.onerror = reject;
            next.src = url.href;
          }),
          new Promise((_, reject) => { this.timeout = setTimeout(reject, 15000); })
        ]);
        await next.decode();
        if (requestId !== this.requestId || !this.dialog.open) return;
        this.image.replaceWith(next);
        this.image = next;
        this.count.textContent = `${this.index + 1} / ${this.photos.length}`;
        this.feedback.hidden = true;
      } catch {
        if (requestId !== this.requestId || !this.dialog.open) return;
        this.feedback.classList.add('is-error');
        this.status.textContent = this.dataset.error;
        this.retry.hidden = false;
      } finally {
        if (requestId === this.requestId) {
          clearTimeout(this.timeout);
          this.stage.removeAttribute('aria-busy');
        }
      }
    }

    disconnectedCallback() {
      this.requestId++;
      this.controller?.abort();
      this.controller = null;
      this.trackObserver?.disconnect();
      clearTimeout(this.timeout);
      this.dialog?.close();
    }
  });
}
